/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
} from 'react-native';
import { isWeb, fullWidth, isMobileWeb, isAndroid } from '@global/screens';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { formatISO, addDays, endOfDay, startOfDay } from 'date-fns';
import Toast from 'react-native-toast-message';
import {
  parseDate,
  currency,
  exportCsvApi,
  isVn,
  firstLetter,
  deSlug,
  getSalesOrderExpiryReminder,
} from '@global/helpers';
import Colors from '@global/colors';
import * as Layout from '@components/screen/Layout';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import SociollaText from '@components/sociolla/SociollaText';
import DatePicker from '@components/form/DatePicker';
import StatusPicker from '@components/form/StatusPicker';
import Image from '@components/global/Image';
import StatusBadge from '@components/global/StatusBadge';
import ReminderNotice from '@components/global/ReminderNotice';
import { showPopup } from '@/store/modal';
import CheckBox from '@components/form/CheckBox';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import CustomerSearch from '@components/form/search/CustomerSearch';
import ModalFull from '@components/modal/ModalFull';
import ModalUpload from '@components/modal/ModalUpload';
import Button from '@components/form/Button';
import { showAlert } from '@/store/global';

import { MsB2b } from '@api/ms-b2b';
import { MsOrder } from '@api/ms-order';
import { filterAdded, paramsReseted } from '@/store/params';
import templateId from '@/assets/csv/template_import_sales_order_id';
import templateVn from '@/assets/csv/template_import_sales_order_vn';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetSalesOrderListQuery,
  useGetSalesOrderCountQuery,
  useGetSalesOrderInfiniteListInfiniteQuery,
  useGetSalesOrderDashboardListQuery,
  useGetSalesOrderDashboardCountQuery,
  useGetSalesOrderDashboardInfiniteListInfiniteQuery,
  useGetSalesOrderSalesInvoiceDashboardListQuery,
  useGetSalesOrderSalesInvoiceDashboardCountQuery,
  useGetSalesOrderSalesInvoiceDashboardInfiniteListInfiniteQuery,
} from '@api/listApi';

// ─────────────────────────────────────────────────────────────────────────────
// Constants & pure helpers (module scope — no component state)
// ─────────────────────────────────────────────────────────────────────────────

const dimension = Dimensions.get('screen');

const tableStructure = {
  customer: {
    head: 'Customer Name',
    body: 'customer.name',
    textStyle: { fontFamily: 'figtree-medium' },
  },
  reference_code: {
    head: 'Order Number',
    body: 'reference_code',
    type: 'slot',
  },
  sales_team: {
    head: 'Sales Team',
    body: 'user.team_name',
    hide: true,
  },
  order_date: {
    head: 'Order Date',
    type: 'date',
    body: 'order_date',
  },
  total_amount: {
    head: 'Total Value',
    type: 'currency',
    symbol: true,
    body: 'total_amount',
  },
  status: {
    head: 'Status',
    body: 'status',
    type: 'slot',
  },
  invoice_status: {
    head: 'Invoice Status',
    type: 'status',
    body: 'invoice_status',
  },
};

const searchForOptions = [
  { label: 'Order Number', value: 'order_number' },
  { label: 'Customer Name', value: 'customer_name' },
  { label: 'Customer Code', value: 'customer_code' },
  { label: 'Customer PO Number', value: 'customer_po_number' },
];

const DEFAULT_STATUSES = [
  { name: 'Draft', value: 'draft' },
  { name: 'Quotation', value: 'quotation' },
  { name: 'Pending Approval', value: 'need_approval' },
  { name: 'Approved', value: 'approved' },
  { name: 'Cancelled', value: 'cancelled' },
];

const invoice_statuses = [
  { name: 'No Invoice Yet', value: 'nothing_to_invoice' },
  { name: 'Partially Invoiced', value: 'to_invoice' },
  { name: 'Fully Invoiced', value: 'fully_invoiced' },
  { name: 'Fully Paid', value: 'fully_paid' },
];

const channels = [
  { name: 'GT', value: 'gt' },
  { name: 'MT', value: 'mt' },
];

const channelsExport = [
  { name: 'All', value: 'all' },
  { name: 'GT', value: 'gt' },
  { name: 'MT', value: 'mt' },
];

const statusesExport = [
  { name: 'All', value: 'all' },
  { name: 'Draft', value: 'draft' },
  { name: 'Quotation', value: 'quotation' },
  { name: 'Need Approval', value: 'need_approval' },
  { name: 'Approved', value: 'approved' },
  { name: 'Cancelled', value: 'cancelled' },
];

// Build the status filter options (adds Pending-Approval-by-role from the SO
// matrix approval). Reference data — fetched once, not per search.
const fetchMatrixStatuses = async () => {
  const matrix = await MsB2b.getMatrixApproval({
    params: { filter: { applicable_for: 'sales_order' } },
  });
  const tempStatuses = [
    { name: 'Draft', value: 'draft' },
    { name: 'Quotation', value: 'quotation' },
    { name: 'Pending Approval', value: 'need_approval' },
  ];
  if (matrix?.data?.data?.length) {
    const matrixSo = matrix.data.data[0];
    const approvers = [];
    for (const rule of matrixSo?.rule || []) {
      const ruleApprovers = (rule?.approver || []).map((a) => a.role);
      approvers.push(...ruleApprovers);
    }
    const uniqueApprovers = [...new Set(approvers)].map((a) => ({
      name: `Pending Approval By ${firstLetter(deSlug(a))}`,
      value: `need_approval_role_${a}`,
    }));
    tempStatuses.push(...uniqueApprovers);
  }
  tempStatuses.push(
    { name: 'Approved', value: 'approved' },
    { name: 'Cancelled', value: 'cancelled' },
  );
  return tempStatuses;
};

// ─────────────────────────────────────────────────────────────────────────────

const SalesOrderView = (props) => {
  // ── selectors ──
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();
  const userData = useSelector((state) => state.auth.user);
  const showOrdersExport = useSelector(
    (state) => !!state.modal.showOrdersExport,
  );
  const showOrdersExportOptions = useSelector(
    (state) => !!state.modal.showOrdersExportOptions,
  );
  const showImportModalSO = useSelector(
    (state) => !!state.modal.showImportModalSO,
  );
  const showFilter = useSelector((state) => state.modal.showFilter);
  // expiry reminder copy is translated while building rows, so rebuild them on language change
  const currentLanguage = useSelector((state) => state.language.selected);

  // ── local state ──
  const [exporting, setExporting] = useState(false);
  const [exportTemplate, setExportTemplate] = useState('detail');
  const [filterExport, setFilterExport] = useState({
    from_date: '',
    to_date: '',
    sales_team: '',
    channel: '',
    customer: '',
    status: '',
    is_clearance: false,
    cash_before_delivery: false,
  });
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [draftFilter, setDraftFilter] = useState({});
  const [draftSalesTeam, setDraftSalesTeam] = useState('');

  // ── derived ──
  // Mode (from the route params it was opened with).
  const isFromHome = Boolean(props?.route?.params?.isHome);
  const isSalesInvoiceDashboard = Boolean(
    props?.route?.params?.isSalesInvoiceDashboard,
  );
  const customerId = props?.route?.params?.customerId;
  const isCustomer = Boolean(customerId);
  const mode = isSalesInvoiceDashboard
    ? 'salesInvoice'
    : isFromHome
    ? 'dashboard'
    : 'normal';

  // Mode-selected resource hooks. `mode` is derived from route params and is stable
  // for a given screen instance, so hook identity never changes across renders.
  const listHook =
    mode === 'salesInvoice'
      ? useGetSalesOrderSalesInvoiceDashboardListQuery
      : mode === 'dashboard'
      ? useGetSalesOrderDashboardListQuery
      : useGetSalesOrderListQuery;
  const countHook =
    mode === 'salesInvoice'
      ? useGetSalesOrderSalesInvoiceDashboardCountQuery
      : mode === 'dashboard'
      ? useGetSalesOrderDashboardCountQuery
      : useGetSalesOrderCountQuery;
  const infiniteHook =
    mode === 'salesInvoice'
      ? useGetSalesOrderSalesInvoiceDashboardInfiniteListInfiniteQuery
      : mode === 'dashboard'
      ? useGetSalesOrderDashboardInfiniteListInfiniteQuery
      : useGetSalesOrderInfiniteListInfiniteQuery;

  const scopeKey =
    mode === 'salesInvoice'
      ? 'SalesOrderSalesInvoiceDashboard'
      : mode === 'dashboard'
      ? 'SalesOrderDashboard'
      : isCustomer
      ? `SalesOrder-customer-${customerId}`
      : undefined; // normal → route name

  // UI filter shape → API query filter (was the inline transform in the old finalParam):
  //  - search box writes `reference_code` (+ `search_for`) → keyword + keyword_for
  //  - `need_approval_role_<role>` status → status:need_approval + current_approval
  //  - sales team array → user.team_id: { $in: [id] }
  //  - customer mode injects customer.id (routes to ES via the resource's hasFilter switch)
  //  - dashboard / sales-invoice add their flag once a filter is present (ES path)
  const toApiFilter = (rawFilter = {}) => {
    const filter = {};
    const stored = rawFilter.reference_code ?? rawFilter.keyword;
    const kw = stored?.$regex ?? stored;
    if (kw) {
      filter.keyword = kw;
      const sf = rawFilter.search_for;
      if (sf) {
        filter.keyword_for = sf?.value ?? sf;
      }
    }
    [
      'order_date',
      'status',
      'created_at',
      'invoice_status',
      'is_clearance',
      'channel',
      'cash_before_delivery',
    ].forEach((k) => {
      const v = rawFilter[k];
      if (v !== undefined && v !== null && v !== '') {
        filter[k] = v;
      }
    });
    if (
      typeof filter.status === 'string' &&
      filter.status.startsWith('need_approval_role_')
    ) {
      filter.current_approval = filter.status.replace(
        'need_approval_role_',
        '',
      );
      filter.status = 'need_approval';
    }
    if (rawFilter['user.team_id']?.length) {
      filter['user.team_id'] = {
        $in: rawFilter['user.team_id'].map((el) => el.id),
      };
    }
    if (isCustomer && customerId) {
      filter['customer.id'] = customerId;
    }
    const hasUserFilter = Object.keys(filter).length > 0;
    if (hasUserFilter && mode === 'dashboard') {
      filter.is_dashboard = true;
    }
    if (hasUserFilter && mode === 'salesInvoice') {
      filter.is_sales_invoice = true;
    }
    return filter;
  };

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook,
    countHook,
    infiniteHook,
    toFilter: toApiFilter,
    scopeKey,
  });

  // ── memoized values ──
  // Current search text / field, derived from the applied filter.
  const searchValue = useMemo(() => {
    const k = params.filter?.reference_code;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);
  const searchForValue = useMemo(() => {
    return (
      params.filter?.search_for || {
        label: 'Order Number',
        value: 'order_number',
      }
    );
  }, [params.filter]);
  const isFiltered = useMemo(
    () => Object.keys(params.filter || {}).length > 0,
    [params.filter],
  );

  // Web = flat array; mobile = paged infinite result. id is already mapped by the factory.
  // Each row carries its draft-expiry reminder (B2B-5937); expired rows get the reminder
  // background (web Table applies `row.style`).
  const rows = useMemo(() => {
    const data = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return data.map((row) => {
      const expiryReminder = getSalesOrderExpiryReminder(row);
      return {
        ...row,
        expiry_reminder: expiryReminder,
        style:
          expiryReminder == 'Expired'
            ? { backgroundColor: Colors.reminderBannerBg }
            : undefined,
      };
    });
  }, [list.data, currentLanguage]);

  // ── effects ──
  // Build the status filter options once (adds Pending-Approval-by-role from the
  // SO matrix approval). Reference data, not per search.
  useEffect(() => {
    let mounted = true;
    fetchMatrixStatuses()
      .then((tempStatuses) => {
        if (mounted) {
          setStatuses(tempStatuses);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Seed the staged filter draft from the applied filter whenever the modal opens.
  useEffect(() => {
    if (showFilter) {
      const applied = params.filter || {};
      setDraftFilter(applied);
      setDraftSalesTeam(applied['user.team_id'] || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── handlers ──
  const customSalesTeamFilter = () => {
    if (userData?.sales_team?.length) {
      return {
        _id: { $in: userData.sales_team.map((team) => team.id) },
      };
    } else {
      return {};
    }
  };

  const draftDateHandler = (value, type) => {
    setDraftFilter((f) => ({
      ...f,
      order_date: {
        ...f.order_date,
        [type]: new Date(value).toISOString(),
      },
    }));
  };

  // Seed the export form from the applied list filter (scoped params hold the UI
  // filter shape: sales team = array of team objects, dates = { $gte, $lte }).
  const openExportOptions = () => {
    setFilterExport({
      ...filterExport,
      sales_team: params.filter?.['user.team_id'] || '',
      channel: params.filter?.channel || '',
      status: params.filter?.status || '',
      from_date: params.filter?.order_date?.$gte || '',
      to_date: params.filter?.order_date?.$lte || '',
      is_clearance: params.filter?.is_clearance || false,
      cash_before_delivery: params.filter?.cash_before_delivery || false,
    });
    dispatch(showPopup({ name: 'showOrdersExportOptions', value: true }));
  };

  const onSelectExportTemplate = (template) => {
    setExportTemplate(template);
    dispatch(showPopup({ name: 'showOrdersExportOptions', value: false }));
    dispatch(showPopup({ name: 'showOrdersExport', value: true }));
  };

  const onExport = async () => {
    setExporting(true);
    try {
      if (!filterExport.from_date || !filterExport.to_date) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter date must be filled',
            isConfirm: false,
            testID: 'sales-order-export-no-date',
          }),
        );
        setExporting(false);
        return;
      }
      if (!filterExport.sales_team?.length) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter sales team must be filled',
            isConfirm: false,
            testID: 'sales-order-export-no-sales-team',
          }),
        );
        setExporting(false);
        return;
      }
      const payload = {
        order_date: {
          $gte: filterExport.from_date,
          $lte: filterExport.to_date,
        },
      };
      if (filterExport.sales_team?.[0]?.id != 'all') {
        payload['sales_team.id'] = {
          $in: filterExport.sales_team.map((st) => st.id),
        };
      }
      if (filterExport.channel != 'all') {
        payload['customer.market_type.code'] = {
          $in: [
            filterExport.channel.toUpperCase(),
            filterExport.channel.toLowerCase(),
          ],
        };
      }
      if (filterExport.customer && filterExport.customer.length > 0) {
        const customerIds = filterExport.customer
          .filter((c) => c.id !== 'all')
          .map((c) => c.id);
        if (customerIds.length > 0) {
          payload['customer.id'] = { $in: customerIds };
        }
      }
      if (filterExport.status && filterExport.status !== 'all') {
        payload['status'] = filterExport.status;
      }
      if (filterExport.is_clearance) {
        payload['is_clearance'] = true;
      }
      if (filterExport.cash_before_delivery) {
        payload['cash_before_delivery'] = true;
      }
      await exportCsvApi(
        exportTemplate == 'summary' ? 'orders-summary' : 'orders',
        payload,
        {
          isDownload: false,
          message: 'Export in progress, the result will be sent to your email.',
        },
      );
      setExporting(false);
    } catch (error) {
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'System error occurred',
          isConfirm: false,
          testID: 'sales-order-export-error',
        }),
      );
      setExporting(false);
    }
  };

  const gotToDetail = (data) => {
    let goBackKey = '';
    if (
      props?.route?.params?.customerId &&
      props?.route?.params?.titleScreen &&
      props?.route?.key
    ) {
      goBackKey = props.route.key;
    }
    openChild('SalesOrderDetailScreen', {
      titleScreen: data.reference_code ? data.reference_code : '-',
      name: data.customer && data.customer.name ? data.customer.name : '-',
      id: data._id,
      go_back_key: goBackKey,
      order_type: data.type,
      isFilter: isFiltered,
    });
  };

  // handling back to customer detail / home page (customer + dashboard modes only)
  const goBackScreen = () => {
    if (isFromHome) {
      props.navigation.replace('SalesOrderScreen', {
        customerId: '',
        titleScreen: '',
      });
      props.navigation.navigate('HomeNavigator');
    } else if (
      props?.route?.params?.customerId &&
      props?.route?.params?.titleScreen
    ) {
      const cid = JSON.parse(JSON.stringify(props.route.params.customerId));
      const titleScreen = JSON.parse(
        JSON.stringify(props.route.params.titleScreen),
      );
      props.navigation.replace('SalesOrderScreen', {
        customerId: '',
        titleScreen: '',
      });
      props.navigation.navigate('CustomerNavigator', {
        screen: 'CustomerDetailScreen',
        params: {
          id: cid,
          name: titleScreen,
        },
      });
    }
  };

  const handleImport = async (file) => {
    try {
      await MsOrder.importSo({ url: file });
      Toast.show({ text1: 'Import Create SO In Progress', type: 'success' });
      dispatch(showPopup({ name: 'showImportModalSO', value: false }));
    } catch (error) {
      Toast.show({
        text1: 'Something Wrong, please try again.',
        type: 'error',
      });
    }
    dispatch(showPopup({ name: 'isLoading', value: false }));
  };

  // ── render ──
  return (
    <Layout.Container testID="sales-order-screen">
      {isWeb && (props?.route?.params?.customerId || isFromHome) ? (
        <Layout.Back
          title={
            props?.route?.params?.titleScreen
              ? props?.route?.params?.titleScreen
              : 'Customer'
          }
          onCallback={() => goBackScreen()}
        />
      ) : (
        false
      )}
      {isWeb ? (
        <Layout.Title title="Sales Order">
          <View style={{ flexDirection: 'row' }}>
            <Button
              testID="sales-order-upload-csv-btn"
              type="default"
              rounded={true}
              buttonStyle={{ width: 110, marginRight: 10 }}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.black }}>
                  Upload CSV
                </SociollaText>
              }
              onPress={() => {
                dispatch(showPopup({ name: 'showImportModalSO', value: true }));
              }}
            />
            <Button
              testID="sales-order-export-btn"
              type="primary"
              rounded={true}
              buttonStyle={{ width: 90, marginRight: 10 }}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  Export
                </SociollaText>
              }
              onPress={openExportOptions}
            />
          </View>
        </Layout.Title>
      ) : (
        false
      )}
      {isWeb ? (
        <Table
          testID="sales-order-web-table"
          filterName={scope}
          name="sales-order"
          structures={tableStructure}
          loading={list.isLoading}
          refetching={list.isFetching}
          data={rows}
          searchKey="reference_code"
          searchPlaceholder="Search by..."
          searchValue={searchValue}
          searchWidth={450}
          hasCallback={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          onClickRow={({ data }) => gotToDetail(data)}
          searchForOptions={searchForOptions}
          searchForValue={searchForValue}
          slot={{
            reference_code: ({ data, index }) => (
              <View key={index}>
                <SociollaText style={{ fontSize: 14 }}>
                  {data.reference_code || '-'}
                </SociollaText>
                <ReminderNotice
                  testID={`sales-order-expiry-reminder-${index}`}
                  title={data.expiry_reminder}
                />
              </View>
            ),
            status: ({ data, index }) => (
              <View key={index} style={{ alignItems: 'center' }}>
                <StatusBadge status={data.status} />
                {data.status == 'need_approval' && data.current_approval ? (
                  <SociollaText
                    style={{
                      fontSize: 10,
                      marginTop: 5,
                      textAlign: 'center',
                    }}>
                    By: {firstLetter(deSlug(data.current_approval))}
                  </SociollaText>
                ) : (
                  false
                )}
              </View>
            ),
          }}
        />
      ) : (
        <TableMobile
          testID="sales-order-mobile-table"
          filterName={scope}
          searchKey="reference_code"
          placeholder="Search by..."
          searchValue={searchValue}
          searchForOptions={searchForOptions}
          searchForValue={searchForValue}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              testID={`sales-order-list-item-${index}`}
              activeOpacity={0.6}
              style={[styles.orderItem, data.style]}
              onPress={() => gotToDetail(data)}
              key={index}>
              <View
                style={[styles.orderItemSection, { alignItems: 'flex-start' }]}>
                <SociollaText
                  type="medium"
                  style={{ fontSize: 15, width: fullWidth - 140 }}>
                  {data.customer && data.customer.name
                    ? data.customer.name
                    : '-'}
                </SociollaText>
                <View>
                  <StatusBadge status={data.status} />
                  {data.status == 'need_approval' && data.current_approval ? (
                    <SociollaText
                      style={{
                        fontSize: 10,
                        marginTop: 5,
                        textAlign: 'center',
                      }}>
                      By: {firstLetter(deSlug(data.current_approval))}
                    </SociollaText>
                  ) : (
                    false
                  )}
                </View>
              </View>
              <SociollaText
                style={{
                  marginTop: 12,
                  marginBottom: 10,
                  fontSize: 14,
                  color: Colors.grey2,
                }}>
                {data.reference_code ? data.reference_code : '-'}
              </SociollaText>
              <View style={styles.orderItemSection}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Image
                    style={{
                      width: 23,
                      height: 16,
                      marginLeft: -5,
                      marginRight: 5,
                    }}
                    width={23}
                    height={16}
                    svg={true}
                    source={require('@assets/img/icons/menu/calendar.svg')}
                  />
                  <SociollaText style={{ fontSize: 12, color: Colors.grey2 }}>
                    {data.order_date ? parseDate(data.order_date) : '-'}
                  </SociollaText>
                </View>
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.blue1 }}>
                  {currency(data.total_amount, true)}
                </SociollaText>
              </View>
              <ReminderNotice
                testID={`sales-order-expiry-reminder-${index}`}
                title={data.expiry_reminder}
                style={{ marginTop: 12 }}
              />
            </TouchableOpacity>
          )}
        />
      )}
      {/* Filter Container */}
      <Layout.Filter
        name="sales-order"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          setDraftSalesTeam('');
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Sales Team
          </SociollaText>
          <SalesTeamSearch
            value={draftSalesTeam}
            placeholder="Select Sales Team"
            multiple={true}
            hideLabel
            customFilter={customSalesTeamFilter()}
            onChange={(value) => {
              setDraftSalesTeam(value);
              setDraftFilter((f) => ({ ...f, 'user.team_id': value }));
            }}
          />
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 20 }}>
            Sales Order Type
          </SociollaText>
          <View
            style={{
              flexDirection: 'row',
              display: 'flex',
              justifyContent: 'space-around',
            }}>
            <View style={{ flexDirection: 'row', display: 'flex' }}>
              <CheckBox
                value={draftFilter['is_clearance']}
                checked={draftFilter['is_clearance']}
                onChange={() => {
                  setDraftFilter((f) => ({
                    ...f,
                    is_clearance: f['is_clearance'] ? false : true,
                  }));
                }}></CheckBox>
              <SociollaText>Clearance</SociollaText>
            </View>
            <View style={{ flexDirection: 'row', display: 'flex' }}>
              <CheckBox
                value={draftFilter['cash_before_delivery']}
                checked={draftFilter['cash_before_delivery']}
                onChange={() => {
                  setDraftFilter((f) => ({
                    ...f,
                    cash_before_delivery: f['cash_before_delivery']
                      ? false
                      : true,
                  }));
                }}></CheckBox>
              <SociollaText>Cash Before Delivery</SociollaText>
            </View>
          </View>
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Channel
          </SociollaText>
          <StatusPicker
            label=""
            options={channels}
            value={draftFilter['channel']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, channel: val.value }))
            }
          />
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Date
          </SociollaText>
          <View style={{ width: '100%', flexDirection: 'row' }}>
            <DatePicker
              testID="sales-order-filter-start-date"
              label="Start Date"
              name="order_date"
              style={{
                marginRight: isWeb ? 10 : 0,
                width: isWeb ? 'auto' : '50%',
              }}
              disableAfterDate={draftFilter.order_date?.$lte}
              value={draftFilter.order_date?.$gte}
              onChange={(value) => draftDateHandler(value, '$gte')}
            />
            <DatePicker
              testID="sales-order-filter-end-date"
              label="End Date"
              name="order_date"
              style={{ width: isWeb ? 'auto' : '50%' }}
              value={draftFilter.order_date?.$lte}
              disableBeforeDate={draftFilter.order_date?.$gte}
              onChange={(value) => draftDateHandler(value, '$lte')}
            />
          </View>
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Status
          </SociollaText>
          <StatusPicker
            label=""
            options={statuses}
            value={draftFilter['status']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, status: val.value }))
            }
          />
        </Layout.FilterItem>
        <Layout.FilterItem lastItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Invoice Status
          </SociollaText>
          <StatusPicker
            label=""
            options={invoice_statuses}
            value={draftFilter['invoice_status']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, invoice_status: val.value }))
            }
          />
        </Layout.FilterItem>
      </Layout.Filter>
      <ModalFull
        testID="sales-order-export-options-modal"
        title="Export Orders Options"
        noTranslate={true}
        show={showOrdersExportOptions}
        maxWidth={isWeb && !isMobileWeb ? 500 : '100%'}
        onClose={() =>
          dispatch(
            showPopup({
              name: 'showOrdersExportOptions',
              value: false,
            }),
          )
        }>
        <View
          testID="sales-order-export-options-content"
          style={styles.exportOptions}>
          <TouchableOpacity
            testID="sales-order-export-detail-btn"
            activeOpacity={0.6}
            style={styles.itemOption}
            onPress={() => onSelectExportTemplate('detail')}>
            <Image
              style={{ width: 35, height: 43 }}
              width={35}
              height={43}
              source={require('@assets/img/icons/add-from-csv.png')}
            />
            <SociollaText style={styles.itemText}>
              Export Detail (SKU Level)
            </SociollaText>
          </TouchableOpacity>
          <TouchableOpacity
            testID="sales-order-export-summary-btn"
            activeOpacity={0.6}
            style={styles.itemOption}
            onPress={() => onSelectExportTemplate('summary')}>
            <Image
              style={{ width: 35, height: 43 }}
              width={35}
              height={43}
              source={require('@assets/img/icons/add-from-csv.png')}
            />
            <SociollaText style={styles.itemText}>
              Export Summary (Order Level)
            </SociollaText>
          </TouchableOpacity>
        </View>
      </ModalFull>
      <ModalFull
        testID="sales-order-export-modal"
        title={
          exportTemplate == 'summary'
            ? 'Export Orders Summary'
            : 'Export Orders'
        }
        noTranslate={true}
        show={showOrdersExport}
        onClose={() =>
          dispatch(showPopup({ name: 'showOrdersExport', value: false }))
        }>
        <View
          style={{
            height: isWeb
              ? 'calc(100vh - 150px)'
              : isMobileWeb
              ? 'calc(100dvh - 150px)'
              : dimension.height - (isAndroid ? 140 : 110),
          }}>
          <ScrollView
            contentContainerStyle={{
              width: '100%',
              ...(!isWeb ? { paddingBottom: 90 } : {}),
            }}>
            <Layout.FormWrapper style={{ padding: 15, height: '100%' }}>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Sales Team
                </SociollaText>
                <SalesTeamSearch
                  value={filterExport.sales_team}
                  placeholder="Select Sales Team"
                  multiple={true}
                  hideLabel
                  customFilter={customSalesTeamFilter()}
                  onChange={(value) => {
                    setFilterExport({ ...filterExport, sales_team: value });
                  }}
                  allTeams
                />
              </Layout.FormGroup>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 20 }}>
                  Sales Order Type
                </SociollaText>
                <View
                  style={{
                    flexDirection: 'row',
                    display: 'flex',
                    justifyContent: 'space-around',
                  }}>
                  <View style={{ flexDirection: 'row', display: 'flex' }}>
                    <CheckBox
                      value={filterExport.is_clearance}
                      checked={filterExport.is_clearance}
                      onChange={() => {
                        setFilterExport({
                          ...filterExport,
                          is_clearance: !filterExport.is_clearance,
                        });
                      }}></CheckBox>
                    <SociollaText>Clearance</SociollaText>
                  </View>
                  <View style={{ flexDirection: 'row', display: 'flex' }}>
                    <CheckBox
                      value={filterExport.cash_before_delivery}
                      checked={filterExport.cash_before_delivery}
                      onChange={() => {
                        setFilterExport({
                          ...filterExport,
                          cash_before_delivery:
                            !filterExport.cash_before_delivery,
                        });
                      }}></CheckBox>
                    <SociollaText>Cash Before Delivery</SociollaText>
                  </View>
                </View>
              </Layout.FormGroup>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Channel
                </SociollaText>
                <StatusPicker
                  label=""
                  options={channelsExport}
                  value={filterExport.channel}
                  trackBy="value"
                  labelKey="name"
                  minWidth={80}
                  onChange={(val) => {
                    setFilterExport({ ...filterExport, channel: val.value });
                  }}
                />
              </Layout.FormGroup>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Customer Name/Code
                </SociollaText>
                <CustomerSearch
                  fields="_id name customer_code"
                  value={filterExport.customer}
                  placeholder="Select Customer"
                  multiple={true}
                  onChange={(value) => {
                    setFilterExport({ ...filterExport, customer: value });
                  }}
                  isAll={true}
                  searchWithCode={true}
                />
              </Layout.FormGroup>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Date
                </SociollaText>
                <View
                  style={{
                    width: '100%',
                    flexDirection: isWeb ? 'row' : 'column',
                    justifyContent: 'space-between',
                  }}>
                  <DatePicker
                    testID="sales-order-export-start-date"
                    label="Start Date"
                    name="created_at"
                    style={{ width: isWeb ? '49%' : '100%' }}
                    value={filterExport.from_date}
                    onChange={(value) => {
                      setFilterExport({
                        ...filterExport,
                        from_date: formatISO(startOfDay(value)),
                      });
                    }}
                  />
                  <DatePicker
                    testID="sales-order-export-end-date"
                    label="End Date"
                    name="created_at"
                    style={{ width: isWeb ? '49%' : '100%' }}
                    value={filterExport.to_date}
                    disableBeforeDate={
                      filterExport.from_date
                        ? filterExport.from_date
                        : addDays(new Date(), 1)
                    }
                    onChange={(value) => {
                      setFilterExport({
                        ...filterExport,
                        to_date: formatISO(endOfDay(value)),
                      });
                    }}
                  />
                </View>
              </Layout.FormGroup>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Status
                </SociollaText>
                <StatusPicker
                  label=""
                  options={statusesExport}
                  value={filterExport.status}
                  trackBy="value"
                  labelKey="name"
                  minWidth={80}
                  onChange={(val) => {
                    setFilterExport({ ...filterExport, status: val.value });
                  }}
                />
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollView>
          <View
            style={{
              width: '100%',
              flexDirection: 'row',
              alignItems: 'flex-end',
              justifyContent: isWeb ? 'end' : 'space-between',
              paddingTop: isWeb ? 20 : 15,
              paddingBottom:
                (isWeb ? 20 : 15) + (isMobileWeb ? insets.bottom : 0),
              paddingHorizontal: isWeb ? 20 : 15,
              backgroundColor: Colors.white,
              ...(!isWeb
                ? {
                    position: isMobileWeb ? 'fixed' : 'absolute',
                    bottom: 0,
                    right: 0,
                    left: 0,
                  }
                : {}),
            }}>
            <Button
              testID="sales-order-export-cancel-btn"
              rounded={true}
              type="primary"
              buttonStyle={{
                width: isWeb
                  ? 100
                  : dimension.width / 2 - (isMobileWeb ? 20 : 25),
                backgroundColor: Colors.white,
                marginRight: 10,
              }}
              title={
                <SociollaText type="medium" style={{ fontSize: 14 }}>
                  Cancel
                </SociollaText>
              }
              onPress={() =>
                dispatch(showPopup({ name: 'showOrdersExport', value: false }))
              }
            />
            <Button
              testID="sales-order-export-submit-btn"
              rounded={true}
              type="primary"
              buttonStyle={{
                width: isWeb
                  ? 100
                  : dimension.width / 2 - (isMobileWeb ? 20 : 25),
              }}
              disabledStyle={{ width: 90 }}
              disabled={exporting}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  {exporting ? 'Loading...' : 'Export'}
                </SociollaText>
              }
              onPress={() =>
                onExport().then(() => {
                  dispatch(
                    showPopup({ name: 'showOrdersExport', value: false }),
                  );
                })
              }
            />
          </View>
        </View>
      </ModalFull>

      <ModalUpload
        testID="sales-order-import-modal"
        show={showImportModalSO}
        onClose={() =>
          dispatch(showPopup({ name: 'showImportModalSO', value: false }))
        }
        templateUrl={isVn() ? templateVn : templateId}
        onUpload={handleImport}
        autoUploadS3={true}
        name={`sales_order${isVn() ? '_vn' : ''}`}
        validHeader={[
          'order',
          'customer_code',
          'delivery_name',
          'expiration_date',
          'pricelist',
          'customer_po_number',
          'customer_po_date',
          'cash_before_delivery',
          'reference_ean',
          'quantity',
        ]}
      />
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  exportOptions: {
    width: '100%',
    paddingHorizontal: isWeb ? 25 : 15,
    paddingTop: isWeb ? 25 : 15,
    paddingBottom: isWeb ? 40 : 15,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  itemOption: {
    padding: 15,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 5,
    borderColor: Colors.grey0,
    borderWidth: 1,
    marginHorizontal: isWeb ? 15 : 10,
  },
  itemText: {
    fontSize: 14,
    marginTop: 15,
    width: 150,
    textAlign: 'center',
  },
  orderItem: {
    width: '100%',
    paddingVertical: 20,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  orderItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: fullWidth - 20,
  },
});

// The same SalesOrderScreen route is reused across modes (order card → invoice card
// on return, etc.). Since the RTK Query hooks are selected by mode, we must NOT let a
// live instance swap hooks — key the inner view by mode so a mode change remounts it
// with a fresh, fixed set of hooks (rules of hooks).
const SalesOrderScreen = (props) => {
  const isFromHome = Boolean(props?.route?.params?.isHome);
  const isSalesInvoiceDashboard = Boolean(
    props?.route?.params?.isSalesInvoiceDashboard,
  );
  const mode = isSalesInvoiceDashboard
    ? 'salesInvoice'
    : isFromHome
    ? 'dashboard'
    : 'normal';
  return <SalesOrderView key={mode} {...props} />;
};

export default SalesOrderScreen;
